import { useState } from 'react';
import { Plus } from 'lucide-react';
import type { FxRates, LeadBoardColumn, LeadStage } from '../../lib/leadsApi';
import { convertAmount, formatCurrency } from './leadConstants';
import { LeadCard } from './LeadCard';

export interface StageMove {
  leadId: string;
  fromStageId: string;
  toStage: LeadStage;
  // Final position within the destination column.
  stageOrder: number;
}

interface LeadKanbanBoardProps {
  columns: LeadBoardColumn[];
  pipelineId: string;
  // Currency every value on the board is shown in (picked on the page).
  currency: string;
  // BNR rates used to convert leads kept in another currency.
  rates?: FxRates | null;
  // Map of leadId -> soonest incomplete next-action due date.
  nextActionDueByLead?: Record<string, string | null>;
  onCardClick: (id: string) => void;
  // The parent owns the move so it can show WON/LOST prompts before committing.
  onStageMove: (move: StageMove) => void;
  // Opens the full Create Lead modal with the column's stage pre-selected.
  onAddLead: (stageId: string) => void;
  // Gate the per-column quick-add to match the header button (deny by default).
  canCreate?: boolean;
}

/**
 * Drag-and-drop Kanban board for leads (CLE-191, spec 3.2). Columns are the
 * pipeline's stages (loaded dynamically, never hardcoded). Native HTML5 DnD
 * supports moving a card to another column (stage change) or reordering within
 * a column (stageOrder). Each column header shows the stage name, lead count
 * and summed value, with a per-column quick-add.
 */
export function LeadKanbanBoard({
  columns,
  pipelineId,
  currency,
  rates,
  nextActionDueByLead = {},
  onCardClick,
  onStageMove,
  onAddLead,
  canCreate = false,
}: LeadKanbanBoardProps) {
  void pipelineId; // pipeline scope is owned by the parent / modal.
  const [dragId, setDragId] = useState<string | null>(null);
  const [overStageId, setOverStageId] = useState<string | null>(null);

  // Flat lookup so a drop can find the dragged lead regardless of its column.
  const allLeads = columns.flatMap((c) => c.leads);

  // Column total in the display currency. A lead with no rate for its currency
  // is left out rather than added as if it were the same money.
  function columnTotal(column: LeadBoardColumn): number {
    return column.leads.reduce((sum, l) => {
      if (l.value == null) return sum;
      return sum + (convertAmount(l.value, l.currency, currency, rates) ?? 0);
    }, 0);
  }

  function handleDropOnColumn(column: LeadBoardColumn, beforeLeadId?: string) {
    setOverStageId(null);
    const leadId = dragId;
    setDragId(null);
    if (!leadId) return;
    const lead = allLeads.find((l) => l.id === leadId);
    if (!lead) return;

    // Compute the destination stageOrder. When dropped onto a specific card we
    // insert before it; otherwise append to the end of the column.
    const target = column.leads.filter((l) => l.id !== leadId);
    let index = target.length;
    if (beforeLeadId) {
      const i = target.findIndex((l) => l.id === beforeLeadId);
      if (i !== -1) index = i;
    }

    const sameColumn = lead.stageId === column.stage.id;
    // No-op when dropping in the same place within the same column.
    if (sameColumn) {
      const currentIndex = column.leads.findIndex((l) => l.id === leadId);
      if (currentIndex === index || currentIndex + 1 === index) return;
    }

    onStageMove({
      leadId,
      fromStageId: lead.stageId,
      toStage: column.stage,
      stageOrder: index,
    });
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {columns.map((column) => {
        const isOver = overStageId === column.stage.id;
        return (
          <div
            key={column.stage.id}
            onDragOver={(e) => {
              e.preventDefault();
              setOverStageId(column.stage.id);
            }}
            onDragLeave={() => setOverStageId((s) => (s === column.stage.id ? null : s))}
            onDrop={() => handleDropOnColumn(column)}
            className={`flex w-72 shrink-0 flex-col rounded-lg border bg-gray-50 ${
              isOver ? 'border-brand-400 ring-2 ring-brand-200' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2">
              <span className="flex items-center gap-2 truncate text-sm font-semibold text-gray-700">
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: column.stage.color }}
                />
                <span className="truncate">{column.stage.name}</span>
              </span>
              <span className="shrink-0 text-xs text-gray-500">
                {column.count} · {formatCurrency(columnTotal(column), currency)}
              </span>
            </div>

            <div className="flex flex-1 flex-col gap-2 px-2 pb-2">
              {column.leads.map((lead) => (
                <div
                  key={lead.id}
                  onDragOver={(e) => {
                    // Allow precise insertion before this card.
                    e.preventDefault();
                    e.stopPropagation();
                    setOverStageId(column.stage.id);
                  }}
                  onDrop={(e) => {
                    e.stopPropagation();
                    handleDropOnColumn(column, lead.id);
                  }}
                >
                  <LeadCard
                    lead={lead}
                    nextActionDue={nextActionDueByLead[lead.id] ?? null}
                    displayCurrency={currency}
                    rates={rates}
                    onClick={() => onCardClick(lead.id)}
                    onDragStart={(e) => {
                      setDragId(lead.id);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                  />
                </div>
              ))}

              {canCreate && (
                <button
                  type="button"
                  onClick={() => onAddLead(column.stage.id)}
                  className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                >
                  <Plus size={12} /> Adaugă lead
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
